import { useCallback, useMemo } from "react";
import type { EntityHooks } from "@/components/shared/UniversalCRMTable/types/shared";
import { useStableMutationResult } from "@/lib/hooks/api/shared/stable-entity-hook-results";
import type { SimpleFieldConfig } from "@/components/shared/UniversalCRMTable/types";
import type { RecurringCharge, RecurringChargeFilters } from "@/lib/types/recurring-charges";
import {
  useRecurringCharges,
  useRecurringCharge,
  useCreateRecurringCharge,
  useUpdateRecurringCharge,
  useDeleteRecurringCharge,
} from "./recurring-charges-queries";

const RECURRING_CHARGE_FIELD_CONFIGS: Record<string, SimpleFieldConfig> = {
  id: { type: "text", label: "ID" },
  customer_id: { type: "text", label: "Customer ID" },
  schedule_type: { type: "select", label: "Schedule Type" },
  last_charged_at: { type: "date", label: "Last Charged" },
  next_charge_date: { type: "date", label: "Next Charge Date" },
  metadata: { type: "json", label: "Charge Details" },
  realm_id: { type: "text", label: "Realm ID" },
  admin_email: { type: "text", label: "Admin Email" },
  created_at: { type: "date", label: "Created" },
  updated_at: { type: "date", label: "Updated" },
};

/**
 * Allocated ONCE at module scope — the object is a dependency of
 * `useBuildActionContext`'s `useMemo` and closes over nothing render-scoped.
 * See `lib/hooks/api/shared/contacts-graphql.ts` for the full reasoning.
 */
const RECURRING_CHARGE_ENTITY_HOOKS: EntityHooks<RecurringCharge> = {
  usePaginated: (params?: RecurringChargeFilters) => {
    const { data, loading, error, refetch } = useRecurringCharges(params);
    return useMemo(
      () => ({
        data: data ? { data, totalCount: 0, pageInfo: undefined } : undefined,
        isLoading: loading,
        error: error as Error | null,
        refetch: refetch || (() => {}),
      }),
      [data, loading, error, refetch]
    );
  },
  useInfinite: (params?: RecurringChargeFilters) => {
    const { data, loading, error, refetch } = useRecurringCharges(params);
    return useMemo(
      () => ({
        data: data ? { pages: [{ data, totalCount: 0, hasNextPage: false }] } : undefined,
        isLoading: loading,
        error: error as Error | null,
        refetch: refetch || (() => {}),
        hasNextPage: false,
        isFetchingNextPage: false,
        fetchNextPage: () => {},
      }),
      [data, loading, error, refetch]
    );
  },
  useSingle: (id: string) => {
    const { data, loading, error, refetch } = useRecurringCharge(id);
    return useMemo(
      () => ({
        data: data || undefined,
        isLoading: loading,
        error: error as Error | null,
        refetch: refetch || (() => {}),
      }),
      [data, loading, error, refetch]
    );
  },
  useCreate: () => {
    const { mutateAsync: create, isPending } = useCreateRecurringCharge();
    const mutateAsync = useCallback(
      async (data: Partial<RecurringCharge> & { id?: string }) => {
        if (
          !data.customer_id ||
          !data.schedule_type ||
          !data.next_charge_date ||
          !data.realm_id ||
          !data.admin_email
        ) {
          throw new Error("Missing required recurring charge fields");
        }

        return create({
          customer_id: data.customer_id,
          schedule_type: data.schedule_type,
          next_charge_date: data.next_charge_date,
          metadata: data.metadata,
          realm_id: data.realm_id,
          admin_email: data.admin_email,
          last_charged_at: data.last_charged_at,
        });
      },
      [create]
    );
    return useStableMutationResult(mutateAsync, isPending);
  },
  useUpdate: () => {
    const { mutateAsync: update, isPending } = useUpdateRecurringCharge();
    const mutateAsync = useCallback(
      async (data: Partial<RecurringCharge> & { id: string }) => {
        const { id, ...updates } = data;
        return update({ id, ...updates });
      },
      [update]
    );
    return useStableMutationResult(mutateAsync, isPending);
  },
  useDelete: () => {
    const { mutateAsync, isPending } = useDeleteRecurringCharge();
    return useStableMutationResult(mutateAsync, isPending);
  },
  getFieldConfig: (fieldName: string) => RECURRING_CHARGE_FIELD_CONFIGS[fieldName],
};

export function useRecurringChargeHooksForPage(): EntityHooks<RecurringCharge> {
  return RECURRING_CHARGE_ENTITY_HOOKS;
}
